import { paramTypeToJoiSchemaMap, getSpecSchema } from '@karya/core';
import { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { CodedError, ok, err } from '@karya/error-values';

/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */
/** Fields of a component that are to be treated as metada and don't influence parsing */
const metadataFields = [
    'key',
    'name',
    'description',
    'ctype',
    'dtype',
    'function_id',
    'ltype',
    'messageDisplay',
];

/** Error thrown when a generic task specification does not conform to the expected schema. */
class InvalidTaskSpecificationError extends CodedError {
    constructor(cause, spec) {
        super('InvalidTaskSpecificationError', `The provided task specification is invalid: ${cause}`, {
            spec,
            cause,
        });
    }
}
/** Error thrown when there is an issue parsing the value of a parameter. */
class ValueParseError extends CodedError {
    constructor(details) {
        var _a, _b;
        const message = `Error parsing value for param of type '${details.param.type}': ${(_b = (_a = details.cause) === null || _a === void 0 ? void 0 : _a.message) !== null && _b !== void 0 ? _b : 'Unknown error occurred'}.`;
        super('ValueParseError', message, details);
    }
}
/**
 * Error thrown when a component's `next` reference points to a component
 * that does not exist in the provided specification.
 */
class NextComponentMissingError extends CodedError {
    constructor(details) {
        const specKeys = '[ ' + Object.keys(details.spec).join(', ') + ' ]';
        const message = `Could not find '${details.nextComponentKey}', but it was referenced.
    The next param can only resolve components defined within the spec - ${specKeys}`;
        super('InvalidNextReferenceError', message, details);
    }
}
class InvalidConditionVariableTypeError extends CodedError {
    constructor(details) {
        const { conditionVariableKey, source, itype, value } = details;
        const message = `'${source}.${conditionVariableKey}' was expected to have '${itype}', but the value found was ${value}, having type ${typeof value}`;
        super('InvalidConditionVariableType', message, details);
    }
}
/**
 * Error class representing a failure in resolving a branch based on a condition variable.
 *
 * This error is thrown when a condition variable does not match any of the provided options,
 * making it impossible to resolve a branch for further execution.
 */
class BranchResolutionError extends CodedError {
    /**
     * Constructs a new BranchResolutionError.
     *
     * @param {BranchResolutionErrorDetails} details - Details explaining the error, including the unresolved condition variable and available options.
     */
    constructor(details) {
        const { conditionVariable, options } = details;
        const availableOptions = Object.keys(options).join(', ');
        let message = `Failed to resolve branch for condition variable value '${conditionVariable}'. Available options are: ${availableOptions}.`;
        if (!('default' in options)) {
            message += ' To avoid this error, consider adding a "default" branch option.';
        }
        super('BranchResolutionError', message, details);
    }
}

/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */
/**
 * Gets a value from source based on the provided {@link param}.
 * Assumes that inhput params must always have a value, while output params may be null.
 *
 * @template Param The type of the param being processed.
 *
 * @param param an input or output param, with instructions to obtain blueprint.
 * @param parseSources Object containing all the information and data sources
 * required for parsing component params
 *
 * @returns The value obtained from the specified source, or a {@link ValueParseError} if retrieval or validation fails.
 */
function getValueFromSource(param, parseSources) {
    var _a, _b, _c, _d, _e, _f, _g;
    let source = 'MTA_OUTPUT';
    let isInputParam = false;
    if ('src' in param && param.src === 'CONSTANT') {
        return ok(param.value);
    }
    if ('src' in param) {
        source = param.src;
        isInputParam = true;
    }
    const paramLoc = param.type === 'FILE' ? 'files' : 'data';
    const microtaskInput = (_a = parseSources.mtInput[paramLoc]) !== null && _a !== void 0 ? _a : { data: {}, files: {} };
    const assignmentOutput = (_b = parseSources.mtaOutput[paramLoc]) !== null && _b !== void 0 ? _b : { data: {}, files: {} };
    const microtaskOutput = (_d = (_c = parseSources.mtOutput) === null || _c === void 0 ? void 0 : _c[paramLoc]) !== null && _d !== void 0 ? _d : { data: {}, files: {} };
    const taskParams = (_e = parseSources.taskParams) !== null && _e !== void 0 ? _e : {};
    const workerProfile = (_f = parseSources.workerProfile) !== null && _f !== void 0 ? _f : {};
    const sources = {
        MT_INPUT: microtaskInput,
        MTA_OUTPUT: assignmentOutput,
        MT_OUTPUT: microtaskOutput,
        TASK_PARAM: taskParams,
        ITER: {},
        APP_PARAM: {},
        WORKER_PROFILE: workerProfile,
    };
    const rawValue = (_g = sources[source][param.key]) !== null && _g !== void 0 ? _g : null;
    // Validate value based on param type
    const paramSchema = isInputParam
        ? // Any input param listed in the spec should always have a value
            paramTypeToJoiSchemaMap[param.type].required()
        : // Output params may not have a value yet, so we allow nulls
            // The comonent writing the output is responsible for ensuring the value is valid
            paramTypeToJoiSchemaMap[param.type].optional().allow(null);
    // For data params, directly validate and return
    if (paramLoc === 'data') {
        const { value, error } = paramSchema.validate(rawValue);
        if (error) {
            return err(new ValueParseError({
                param,
                rawValue,
                cause: error,
            }));
        }
        return ok(value);
    }
    // Validate file params with possible fileUrlMap mapping
    const { value: origUri, error: origUriError } = paramSchema.validate(rawValue);
    if (!origUriError) {
        return ok(origUri);
    }
    // If original URI is invalid, try to map using fileUrlMap
    if (parseSources.fileUrlMap && typeof rawValue == 'string' && rawValue in parseSources.fileUrlMap) {
        const { value: mappedUri, error: mappedUriError } = paramSchema.validate(parseSources.fileUrlMap[rawValue]);
        if (mappedUriError) {
            return err(new ValueParseError({
                param,
                rawValue: parseSources.fileUrlMap[rawValue],
                cause: mappedUriError,
            }));
        }
        return ok(mappedUri);
    }
    // If fileUrlMap lookup fails, return the original error
    return err(new ValueParseError({
        param,
        rawValue,
        cause: origUriError,
    }));
}

/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */
/**
 * Parses all the non-reference params of a generic task component.
 *
 * @template T Type of the component to parse.
 *
 * @param component The component to parse.
 * @param parseSources Object containing all the information and data sources
 * required for parsing component params
 *
 * @returns An object containing the parsed values of the params contained within the component
 */
function parseComponent(component, parseSources) {
    // Get and parse Object entries for all component params
    const nonMetadataParams = Object.keys(component).filter((name) => !metadataFields.includes(name));
    const parsedParamEntries = [];
    for (const paramName of nonMetadataParams) {
        const param = component[paramName];
        // NOTE: The reference params need to be handled separately since they
        //       may not be available at this stage, causing early returns.
        if (param.type === 'REF') {
            continue;
        }
        const parseResult = getValueFromSource(param, parseSources);
        if (parseResult.isErr()) {
            // Typecast is required since the value of the error result resolves
            // to unknown here due to generics
            return parseResult;
        }
        parsedParamEntries.push([paramName, parseResult.value]);
    }
    // Typecast is required since the value of the ok result resolves
    return ok(Object.fromEntries(parsedParamEntries));
}

/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */
function getEmptyOutput() {
    return {
        data: {},
        files: {},
    };
}
/**
 * @param props Contains the spec and data sources external to the state of the task Executor
 * @returns A {@link TaskExecutionController} representing the state and functionality exposed by the executor
 */
function useGenericTaskExecutor(props) {
    var _a;
    const [history, setHistory] = useState([]);
    const [mtaOutput, setMtaOutput] = useState(getEmptyOutput());
    const [parseError, setParseError] = useState(null);
    const submitHandlerRef = useRef(null);
    const sources = useMemo(() => {
        var _a, _b;
        return ({
            spec: props.spec,
            mtInput: (_a = props.mtInput) !== null && _a !== void 0 ? _a : { data: {} },
            taskParams: props.params,
            mtaOutput,
            fileUrlMap: props.fileUrlMap,
            // Creates a dummy worker profile
            workerProfile: (_b = props.workerProfile) !== null && _b !== void 0 ? _b : {},
        });
    }, [props.spec, props.mtInput, props.params, mtaOutput, props.fileUrlMap, props.workerProfile]);
    useEffect(() => {
        const initialHistory = props.initialHistory;
        if (props.initialOutput != null) {
            setMtaOutput(props.initialOutput);
        }
        else if (!props.persistOutputAcrossSpecChanges) {
            setMtaOutput(getEmptyOutput());
        }
        if (initialHistory != null && initialHistory.length > 0) {
            setHistory(initialHistory);
            setParseError(null);
            return;
        }
        if (!Object.prototype.hasOwnProperty.call(props.spec, 'start')) {
            setHistory([]);
            return;
        }
        const [params, parseError] = parseComponent(props.spec.start, sources).toTuple();
        if (parseError) {
            setParseError(parseError);
            return;
        }
        const startNode = {
            component: props.spec.start,
            params,
        };
        setHistory([startNode]);
        setParseError(null);
        // We don't want sources and fileUrlMap as a dependencies.
        // They're unstable and changes on every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.spec, props.persistOutputAcrossSpecChanges]);
    useEffect(() => {
        if (props.disableSpecValidation) {
            return;
        }
        const [schema, schemaError] = getSpecSchema(props.spec, {
            failForUnknownComponents: props.failForUnknownComponents,
        }).toTuple();
        if (schemaError) {
            setParseError(schemaError);
            return;
        }
        const { error } = schema.validate(props.spec, { abortEarly: false });
        if (error) {
            setParseError(new InvalidTaskSpecificationError(error, props.spec));
        }
        else {
            setParseError(null);
        }
    }, [props.spec, props.failForUnknownComponents, props.disableSpecValidation]);
    /** Move to the next component in the spec */
    const _goToNext = useCallback(() => {
        const currNode = history.at(-1);
        if (currNode == undefined) {
            return;
        }
        // If current node is END, do nothing
        if (currNode.component.key === 'end') {
            return;
        }
        // This function was invoked via submit handler
        // Since we're moving to the next component, clear the submit handler
        // from the previous component
        if (submitHandlerRef.current) {
            submitHandlerRef.current = null;
        }
        const [nextComponent, error] = getReferencedComponent(currNode.component.next, sources).toTuple();
        if (error) {
            setParseError(error);
        }
        if (nextComponent == undefined) {
            return;
        }
        const parsedNext = parseComponent(nextComponent, sources);
        if (parsedNext.isErr()) {
            setParseError(parsedNext.error);
            return;
        }
        const nextNode = {
            component: nextComponent,
            params: parsedNext.value,
        };
        setHistory(history.concat([nextNode]));
    }, [history, sources]);
    /** Move to the next component, invoking submit handler if set */
    const nextHandler = useCallback(() => {
        if (submitHandlerRef.current) {
            submitHandlerRef.current(_goToNext);
        }
        else {
            _goToNext();
        }
    }, [_goToNext]);
    useEffect(() => {
        const currNode = history.at(-1);
        if (currNode == undefined) {
            return;
        }
        if (currNode.component.key === 'end') {
            return;
        }
        const [reparsedParams, parseError] = parseComponent(currNode.component, sources).toTuple();
        if (parseError) {
            setParseError(parseError);
            return;
        }
        const reparsedNode = {
            component: currNode.component,
            params: reparsedParams,
        };
        const updatedHistory = history.slice(0, -1).concat([reparsedNode]);
        setHistory(updatedHistory);
        // We don't want history and sources as dependencies here. These are unstable and change on every render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mtaOutput]);
    const writeOutput = useCallback((param, value) => {
        const currNode = history.at(-1);
        if (currNode == undefined) {
            return;
        }
        const updatedOutput = Object.assign({}, mtaOutput);
        if (param.type == 'FILE') {
            if (typeof value !== 'string') {
                console.error('Expected a string for file output, but received:', value);
                return;
            }
            updatedOutput.files = Object.assign(Object.assign({}, updatedOutput.files), { [param.key]: value });
        }
        else {
            updatedOutput.data = Object.assign(Object.assign({}, updatedOutput.data), { [param.key]: value });
        }
        setMtaOutput(updatedOutput);
        // Parse current component to snapshot the history state
        const [reparsedParams, parseError] = parseComponent(currNode.component, sources).toTuple();
        if (parseError) {
            setParseError(parseError);
            return;
        }
        const reparsedNode = {
            component: currNode.component,
            params: reparsedParams,
        };
        const updatedHistory = history.slice(0, -1).concat([reparsedNode]);
        setHistory(updatedHistory);
    }, [history, mtaOutput, sources]);
    const currentIndex = history.length - 1;
    const didCompleteExecution = ((_a = history.at(-1)) === null || _a === void 0 ? void 0 : _a.component.key) === 'end';
    const setSubmitHandler = useCallback((handler) => {
        submitHandlerRef.current = handler;
    }, []);
    return {
        history,
        next: nextHandler,
        mtaOutput,
        writeOutput,
        didCompleteExecution,
        currentIndex,
        parseError,
        setSubmitHandler,
    };
}

/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */
/** Resolves the branch name from the condition variable specified in the param */
function resolveBranchName(param, parseSources) {
    // Create a new param to get the value of the condition variable from the itype
    const conditionVariableParam = {
        type: param.itype,
        src: param.src,
        key: param.key,
    };
    const [parsedCondition, conditionParseError] = getValueFromSource(conditionVariableParam, parseSources).toTuple();
    if (conditionParseError) {
        return err(new InvalidConditionVariableTypeError({
            itype: param.itype,
            source: param.src,
            conditionVariableKey: param.key,
            value: conditionParseError.details.rawValue,
        }));
    }
    let branchName;
    // Attempt to resolve the branch name based on the parsed condition
    // - If primitive, use it directly as branch name
    // - If array, use first element as branch name
    // - If none match, check for default branch
    if (['string', 'boolean', 'number'].includes(typeof parsedCondition)) {
        branchName = String(parsedCondition);
    }
    else if (parsedCondition instanceof Array && typeof parsedCondition[0] === 'string') {
        branchName = parsedCondition[0];
    }
    else if ('default' in param.options) {
        branchName = 'default';
    }
    else {
        return err(new BranchResolutionError({
            conditionVariable: parsedCondition,
            options: param.options,
            itype: param.itype,
        }));
    }
    return ok(branchName);
}
/**
 * Gets the spec component referenced to by param of type 'REF'
 *
 * @param param {@link ReferenceInputParam} that contains the key of the referenced component
 * or instructions to resolve it dynamically via a condition variable.
 * @param parseSources Object containing all the information and data sources
 * required for parsing component params
 *
 * @returns The referenced component if found in the spec or null
 */
function getReferencedComponent(param, parseSources) {
    // Key of the component referenced in the param
    let referencedComponentKey = null;
    if (param.src === 'CONSTANT') {
        // Get reference from "CONSTANT" key specified in param.value
        referencedComponentKey = param.value;
    }
    else {
        const branchNameResult = resolveBranchName(param, parseSources);
        if (branchNameResult.isErr()) {
            branchNameResult.error.addContext("When resolving branch name for 'REF' param");
            return err(branchNameResult.error);
        }
        referencedComponentKey = param.options[branchNameResult.value];
    }
    if (referencedComponentKey == null || !(referencedComponentKey in parseSources.spec)) {
        return err(new NextComponentMissingError({
            param,
            spec: parseSources.spec,
            nextComponentKey: referencedComponentKey,
        }));
    }
    return ok(parseSources.spec[referencedComponentKey]);
}

/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */
/**
 * @template Param The type of the param being processed.
 * @param spec Specification to which the component containing the param belogs.
 * @param parseSources Object containing all the information and data sources
 * required for parsing component params
 *
 * @returns For REF-type parameters, returns the referenced component from the spec.
 *          For all other parameter types, returns the resolved value from the appropriate source
 *          (primitive, object, array, or file URL). Returns null if the parameter cannot be resolved.
 *
 */
function parseParam(param, parseSources) {
    if (param.type === 'REF') {
        return getReferencedComponent(param, parseSources);
    }
    return getValueFromSource(param, parseSources);
}

export { getReferencedComponent, getValueFromSource, metadataFields, parseComponent, parseParam, useGenericTaskExecutor };
//# sourceMappingURL=Index.mjs.map
